import { useState, useRef, useCallback } from "react";
import {
  X, MousePointer2, Pencil, Type, Eraser, Highlighter,
  Undo2, Redo2, ZoomIn, ZoomOut, Save, MoveUpRight
} from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

/* ── Template image URLs (local paths to avoid CORS issues with PDF export) ── */
const TEMPLATE_IMAGES = {
  "full-anterior": "/body-charts/full-body-anterior.png",
  "full-posterior": "/body-charts/full-body-posterior.png",
  "left-lateral": "/body-charts/full-body-left-lateral.png",
  "right-lateral": "/body-charts/full-body-right-lateral.png",
  "left-foot": "/body-charts/left-foot.png",
  "right-foot": "/body-charts/right-foot.png",
  "hands": "/body-charts/hands.png",
};

export const TEMPLATES = [
  { id: "full-anterior", name: "Full body anterior" },
  { id: "full-posterior", name: "Full body posterior" },
  { id: "left-lateral", name: "Left lateral" },
  { id: "right-lateral", name: "Right lateral" },
  { id: "left-foot", name: "Left foot" },
  { id: "right-foot", name: "Right foot" },
  { id: "hands", name: "Hands" },
];

export function getTemplateImage(templateId) {
  return TEMPLATE_IMAGES[templateId] || TEMPLATE_IMAGES["full-anterior"];
}

/* ── Inline Chart Preview (for note cards and note editor) ── */
export function BodyChartPreview({ chart, size = "md", onClick }) {
  const img = getTemplateImage(chart.templateId);
  const sizeClass = size === "sm" ? "h-24" : size === "lg" ? "h-64" : "h-40";

  return (
    <div className={`relative ${sizeClass} bg-white rounded-lg border border-[#1A1B1A]/[0.06] overflow-hidden cursor-pointer group`}
      onClick={onClick} data-testid="body-chart-preview">
      <img src={img} alt={chart.templateId} className="w-full h-full object-contain p-1" />
      {/* Overlay drawings + texts */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 700" preserveAspectRatio="xMidYMid meet">
        {chart.drawings?.map((d, i) => (
          <polyline key={i} points={d.points.map(p => `${p.x},${p.y}`).join(" ")}
            fill="none" stroke={d.color} strokeWidth={d.width} strokeLinecap="round" strokeLinejoin="round" />
        ))}
        {chart.texts?.map((t, i) => (
          <text key={i} x={t.x} y={t.y} fill={t.color} fontSize="12" fontWeight="500" fontFamily="Manrope, sans-serif">{t.text}</text>
        ))}
      </svg>
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
        <span className="opacity-0 group-hover:opacity-100 text-[10px] font-medium text-white bg-black/50 px-2 py-1 rounded-full transition-opacity">Click to view</span>
      </div>
    </div>
  );
}

/* ── Fullscreen Chart Viewer (lightbox) ── */
export function BodyChartLightbox({ chart, onClose }) {
  const img = getTemplateImage(chart.templateId);
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-8" onClick={onClose} data-testid="body-chart-lightbox">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#1A1B1A]/[0.06]">
          <span className="text-sm font-medium text-[#1A1B1A] capitalize" style={H}>{chart.templateId?.replace(/-/g, " ")}</span>
          <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
        </div>
        <div className="relative p-4" style={{ minHeight: 400 }}>
          <img src={img} alt={chart.templateId} className="w-full h-auto max-h-[70vh] object-contain" />
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 700" preserveAspectRatio="xMidYMid meet" style={{ padding: 16 }}>
            {chart.drawings?.map((d, i) => (
              <polyline key={i} points={d.points.map(p => `${p.x},${p.y}`).join(" ")}
                fill="none" stroke={d.color} strokeWidth={d.width} strokeLinecap="round" strokeLinejoin="round" />
            ))}
            {chart.texts?.map((t, i) => (
              <text key={i} x={t.x} y={t.y} fill={t.color} fontSize="14" fontWeight="600" fontFamily="Manrope, sans-serif">{t.text}</text>
            ))}
          </svg>
        </div>
        {chart.texts?.length > 0 && (
          <div className="px-5 py-3 border-t border-[#1A1B1A]/[0.06] flex flex-wrap gap-1.5">
            {chart.texts.map((t, i) => <span key={i} className="text-[10px] bg-[#FAF9F6] px-2 py-0.5 rounded-full text-[#4A4B4A]">{t.text}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Body Chart Editor ── */
export default function BodyChartEditor({ existingChart, readOnly, onSave, onClose }) {
  const [step, setStep] = useState(existingChart ? "editor" : "select");
  const [templateId, setTemplateId] = useState(existingChart?.templateId || "full-anterior");
  const [tool, setTool] = useState("pen");
  const [penColor, setPenColor] = useState("#C4553A");
  const [penWidth, setPenWidth] = useState(2);
  const [drawings, setDrawings] = useState(existingChart?.drawings || []);
  const [texts, setTexts] = useState(existingChart?.texts || []);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState([]);
  const [arrowStart, setArrowStart] = useState(null);
  const [addingText, setAddingText] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [zoom, setZoom] = useState(1);
  const [draggingText, setDraggingText] = useState(null);
  const canvasRef = useRef(null);

  const pushHistory = useCallback(() => {
    setHistory(h => [...h, { drawings: [...drawings], texts: [...texts] }]);
    setRedoStack([]);
  }, [drawings, texts]);

  const undo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setRedoStack(r => [...r, { drawings: [...drawings], texts: [...texts] }]);
    setDrawings(prev.drawings);
    setTexts(prev.texts);
    setHistory(h => h.slice(0, -1));
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setHistory(h => [...h, { drawings: [...drawings], texts: [...texts] }]);
    setDrawings(next.drawings);
    setTexts(next.texts);
    setRedoStack(r => r.slice(0, -1));
  };

  const getPos = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom };
  };

  const handleCanvasMouseDown = (e) => {
    if (readOnly || addingText) return;
    const pos = getPos(e);
    if (!pos) return;

    if (tool === "pointer") {
      // Check if clicking near a text annotation to drag it
      const hitIdx = texts.findIndex(t => Math.abs(t.x - pos.x) < 50 && Math.abs(t.y - pos.y) < 12);
      if (hitIdx !== -1) {
        pushHistory();
        setDraggingText({ index: hitIdx, offsetX: pos.x - texts[hitIdx].x, offsetY: pos.y - texts[hitIdx].y });
      }
      return;
    }
    if (tool === "text") {
      setAddingText(pos);
      setTextInput("");
      return;
    }
    if (tool === "arrow") {
      pushHistory();
      setArrowStart(pos);
      return;
    }
    if (tool === "pen" || tool === "highlight") {
      pushHistory();
      setIsDrawing(true);
      setCurrentPath([pos]);
    }
    if (tool === "eraser") {
      pushHistory();
      const threshold = 15;
      
      // Helper: check if a point is near a line segment (for erasing arrows)
      const pointNearLine = (px, py, x1, y1, x2, y2, thresh) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;
        const lenSq = C * C + D * D;
        const t = lenSq > 0 ? Math.max(0, Math.min(1, (A * C + B * D) / lenSq)) : 0;
        const nearX = x1 + t * C;
        const nearY = y1 + t * D;
        const dist = Math.sqrt((px - nearX) ** 2 + (py - nearY) ** 2);
        return dist < thresh;
      };
      
      setDrawings(prev => prev.filter(d => {
        // For arrows: check if eraser is near the line segment
        if (d.type === "arrow" && d.points.length >= 2) {
          const [start, end] = d.points;
          return !pointNearLine(pos.x, pos.y, start.x, start.y, end.x, end.y, threshold + 5);
        }
        // For pen/highlight: check if eraser is near any point
        return !d.points.some(p => Math.abs(p.x - pos.x) < threshold && Math.abs(p.y - pos.y) < threshold);
      }));
      // Erase texts with a slightly larger hit area
      setTexts(prev => prev.filter(t => !(Math.abs(t.x - pos.x) < 40 && Math.abs(t.y - pos.y) < 15)));
    }
  };

  const handleMouseMove = (e) => {
    if (draggingText !== null) {
      const pos = getPos(e);
      if (pos) {
        setTexts(prev => prev.map((t, i) => i === draggingText.index ? { ...t, x: pos.x - draggingText.offsetX, y: pos.y - draggingText.offsetY } : t));
      }
      return;
    }
    if (!isDrawing) return;
    const pos = getPos(e);
    if (pos) setCurrentPath(p => [...p, pos]);
  };

  const handleMouseUp = (e) => {
    if (draggingText !== null) { setDraggingText(null); return; }
    if (arrowStart) {
      const pos = getPos(e);
      if (pos && (Math.abs(pos.x - arrowStart.x) > 5 || Math.abs(pos.y - arrowStart.y) > 5)) {
        setDrawings(prev => [...prev, { type: "arrow", points: [arrowStart, pos], color: penColor, width: penWidth }]);
      }
      setArrowStart(null);
      return;
    }
    if (!isDrawing) return;
    setIsDrawing(false);
    if (currentPath.length > 1) {
      setDrawings(prev => [...prev, {
        points: currentPath,
        color: tool === "highlight" ? penColor + "40" : penColor,
        width: tool === "highlight" ? penWidth * 4 : penWidth,
        type: tool,
      }]);
    }
    setCurrentPath([]);
  };

  const addText = () => {
    if (!textInput.trim() || !addingText) return;
    pushHistory();
    setTexts(prev => [...prev, { x: addingText.x, y: addingText.y, text: textInput, color: penColor }]);
    setAddingText(null);
    setTextInput("");
  };

  const cancelText = () => { setAddingText(null); setTextInput(""); };

  const handleSave = () => { onSave({ templateId, drawings, texts }); };

  const TOOLS = [
    { id: "pointer", icon: MousePointer2, label: "Select" },
    { id: "pen", icon: Pencil, label: "Draw" },
    { id: "arrow", icon: MoveUpRight, label: "Arrow" },
    { id: "highlight", icon: Highlighter, label: "Highlight" },
    { id: "text", icon: Type, label: "Text" },
    { id: "eraser", icon: Eraser, label: "Erase" },
  ];
  const COLORS = ["#C4553A", "#205B5A", "#496275", "#1A1B1A", "#D97706"];

  // ── Template Selection ──
  if (step === "select") return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="body-chart-template-select">
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-3 flex items-center justify-between">
        <h2 className="text-base font-medium text-[#1A1B1A]" style={H}>Choose body chart template</h2>
        <button onClick={onClose} className="p-1 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {TEMPLATES.map(t => (
            <button key={t.id} onClick={() => { setTemplateId(t.id); setStep("editor"); }} data-testid={`template-${t.id}`}
              className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-3 hover:shadow-md hover:border-[#205B5A]/20 transition-all group text-center">
              <div className="h-36 mb-2 flex items-center justify-center overflow-hidden rounded-lg">
                <img src={getTemplateImage(t.id)} alt={t.name} className="max-h-full max-w-full object-contain" />
              </div>
              <p className="text-xs font-medium text-[#1A1B1A] group-hover:text-[#205B5A]">{t.name}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  // ── Editor ──
  return (
    <div className="fixed inset-0 z-50 bg-[#FAF9F6] flex flex-col" data-testid="body-chart-editor">
      {/* Toolbar */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-4 py-2 flex items-center gap-2 flex-shrink-0">
        <button onClick={onClose} className="p-1.5 text-[#757675] hover:text-[#1A1B1A]"><X className="w-4 h-4" /></button>
        <span className="text-xs font-medium text-[#757675] border-r border-[#1A1B1A]/[0.06] pr-3 mr-1">{TEMPLATES.find(t => t.id === templateId)?.name}</span>

        {!readOnly && (
          <div className="flex items-center gap-0.5 bg-[#FAF9F6] rounded-lg p-0.5">
            {TOOLS.map(t => (
              <button key={t.id} onClick={() => { setTool(t.id); setAddingText(null); }} title={t.label} data-testid={`tool-${t.id}`}
                className={`p-2 rounded-md transition-colors ${tool === t.id ? "bg-white shadow-sm text-[#205B5A]" : "text-[#757675] hover:text-[#4A4B4A]"}`}>
                <t.icon className="w-4 h-4" strokeWidth={1.5} />
              </button>
            ))}
          </div>
        )}

        {!readOnly && (tool === "pen" || tool === "highlight" || tool === "text") && (
          <div className="flex items-center gap-1 ml-2">
            {COLORS.map(c => (
              <button key={c} onClick={() => setPenColor(c)}
                className={`w-5 h-5 rounded-full border-2 transition-all ${penColor === c ? "border-[#1A1B1A] scale-110" : "border-transparent"}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        )}

        {!readOnly && (tool === "pen" || tool === "highlight") && (
          <div className="flex items-center gap-1 ml-2">
            {[1, 2, 3, 4].map(w => (
              <button key={w} onClick={() => setPenWidth(w)}
                className={`w-6 h-6 rounded-md flex items-center justify-center transition-colors ${penWidth === w ? "bg-[#205B5A]/10" : "hover:bg-[#FAF9F6]"}`}>
                <div className="rounded-full bg-[#1A1B1A]" style={{ width: w * 2, height: w * 2 }} />
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1 ml-auto">
          {!readOnly && (
            <>
              <button onClick={undo} disabled={history.length === 0} title="Undo" data-testid="undo-btn" className="p-2 rounded-md text-[#757675] hover:text-[#1A1B1A] disabled:opacity-30"><Undo2 className="w-4 h-4" /></button>
              <button onClick={redo} disabled={redoStack.length === 0} title="Redo" data-testid="redo-btn" className="p-2 rounded-md text-[#757675] hover:text-[#1A1B1A] disabled:opacity-30"><Redo2 className="w-4 h-4" /></button>
            </>
          )}
          <button onClick={() => setZoom(z => Math.min(z + 0.2, 3))} title="Zoom in" className="p-2 rounded-md text-[#757675] hover:text-[#1A1B1A]"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))} title="Zoom out" className="p-2 rounded-md text-[#757675] hover:text-[#1A1B1A]"><ZoomOut className="w-4 h-4" /></button>
          {!readOnly && (
            <button onClick={handleSave} data-testid="save-chart-btn"
              className="ml-2 bg-[#205B5A] text-white px-4 py-1.5 rounded-full text-[11px] font-medium hover:bg-[#174544] flex items-center gap-1">
              <Save className="w-3 h-3" /> Save chart
            </button>
          )}
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex-1 overflow-auto flex items-center justify-center bg-[#F3F2EE] p-4 relative">
        <div ref={canvasRef}
          className="relative bg-white rounded-xl shadow-sm border border-[#1A1B1A]/[0.06]"
          style={{ width: 600 * zoom, height: 700 * zoom, cursor: tool === "pen" || tool === "highlight" ? "crosshair" : tool === "text" ? "text" : tool === "eraser" ? "pointer" : tool === "pointer" ? "default" : "default" }}
          onMouseDown={handleCanvasMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>

          {/* Template image */}
          <img src={getTemplateImage(templateId)} alt={templateId}
            className="absolute inset-0 w-full h-full object-contain p-4 pointer-events-none select-none" draggable={false} />

          {/* Drawing SVG layer */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none">
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto" fill="currentColor">
                <polygon points="0 0, 10 3.5, 0 7" />
              </marker>
            </defs>
            <g style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}>
              {drawings.map((d, i) => d.type === "arrow" ? (
                <line key={i} x1={d.points[0].x} y1={d.points[0].y} x2={d.points[1].x} y2={d.points[1].y}
                  stroke={d.color} strokeWidth={d.width + 1} markerEnd="url(#arrowhead)" style={{ color: d.color }} />
              ) : (
                <polyline key={i} points={d.points.map(p => `${p.x},${p.y}`).join(" ")}
                  fill="none" stroke={d.color} strokeWidth={d.width} strokeLinecap="round" strokeLinejoin="round" />
              ))}
              {isDrawing && currentPath.length > 1 && (
                <polyline points={currentPath.map(p => `${p.x},${p.y}`).join(" ")}
                  fill="none" stroke={tool === "highlight" ? penColor + "40" : penColor}
                  strokeWidth={tool === "highlight" ? penWidth * 4 : penWidth} strokeLinecap="round" strokeLinejoin="round" />
              )}
              {arrowStart && (
                <line x1={arrowStart.x} y1={arrowStart.y} x2={arrowStart.x + 20} y2={arrowStart.y - 20}
                  stroke={penColor} strokeWidth={penWidth + 1} strokeDasharray="4 2" opacity="0.5" />
              )}
              {texts.map((t, i) => (
                <text key={i} x={t.x} y={t.y} fill={t.color} fontSize="12" fontWeight="500" fontFamily="Manrope, sans-serif"
                  style={{ cursor: tool === "pointer" && !readOnly ? "grab" : "default", pointerEvents: tool === "pointer" ? "auto" : "none" }}
                  stroke={draggingText?.index === i ? "#205B5A" : "none"} strokeWidth={draggingText?.index === i ? 0.5 : 0}>{t.text}</text>
              ))}
            </g>
          </svg>
        </div>

        {/* Text annotation popup — OUTSIDE the canvas to avoid pointer-event conflicts */}
        {addingText && (
          <div className="absolute bg-white rounded-xl shadow-xl border border-[#1A1B1A]/[0.08] p-3 z-[60]"
            style={{ left: `calc(50% + ${(addingText.x * zoom - 300 * zoom)}px)`, top: `calc(50% + ${(addingText.y * zoom - 350 * zoom)}px)` }}
            onMouseDown={e => e.stopPropagation()} data-testid="text-annotation-popup">
            <p className="text-[10px] font-medium text-[#757675] mb-1.5">Add text annotation</p>
            <input value={textInput} onChange={e => setTextInput(e.target.value)} autoFocus
              className="w-56 px-3 py-2 text-sm border border-[#1A1B1A]/10 rounded-lg focus:outline-none focus:border-[#205B5A] mb-2"
              placeholder="e.g. Low back pain, Sciatica..."
              data-testid="chart-text-input"
              onKeyDown={e => { if (e.key === "Enter") addText(); if (e.key === "Escape") cancelText(); }} />
            <div className="flex gap-2">
              <button onClick={addText} data-testid="chart-text-add-btn"
                className="text-xs font-medium text-white bg-[#205B5A] px-3 py-1.5 rounded-lg hover:bg-[#174544]">Add</button>
              <button onClick={cancelText} data-testid="chart-text-cancel-btn"
                className="text-xs text-[#757675] hover:text-[#1A1B1A] px-2 py-1.5">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
